import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalField from "work/proposals/proposal-fields/proposal-field";

const MainContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
}));
const TeamContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-around",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));
const TeamList = styled(List)(({ theme }) => ({
  margin: 0,
  marginTop: 0,
  minWidth: theme.spacing(40),
  paddingBottom: theme.spacing(3),
  "&:first-of-type": {
    marginRight: theme.spacing(3),
  },
  [theme.breakpoints.down("lg")]: {
    "&:first-of-type": {
      marginRight: 0,
    },
  },
}));
const TeamListSubheader = styled(ListSubheader)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.common.white,
  color: theme.palette.common.black,
  display: "flex",
  fontSize: "1.3em",
  justifyContent: "space-between",
  padding: 0,
  "& > button": {
    height: "fit-content",
    width: "fit-content",
  },
}));
const NoRowsPlaceholder = styled(Typography)(({ theme }) => ({
  fontSize: "1.3em",
  paddingTop: theme.spacing(1),
}));
const TeamMemberLink = styled(Link)(({ theme }) => ({
  color: "grey",
  cursor: "pointer",
  textDecorationColor: theme.palette.common.black,
}));

type VendorTeamReadonlyProps = {
  className?: string;
  leader: Individual | null;
  members: Individual[];
  hideCommentButton?: boolean;
  commentThreads?: CommentThread[];
  onCommentsClicked?: (proposalField: ProposalField, name?: string) => void;
};

export default function VendorTeamReadOnly(props: Readonly<VendorTeamReadonlyProps>) {
  const { className, leader, members, hideCommentButton, commentThreads, onCommentsClicked } = props;

  const { openDialog } = useDialog();

  /**
   * Opens the user profile dialog for the given individual
   *
   * @param individualId The id of the individual to open the dialog for
   */
  function openUserProfileDialog(individualId: Guid) {
    openDialog({
      component: <ViewIndividualProfile individualId={individualId} />,
      titleStyle: {
        paddingBottom: 0
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: true,
      },
    });
  }

  function renderLeader(leader?: Individual) {
    if (!leader) {
      return (
        <ListItem disableGutters>
          <NoRowsPlaceholder>None</NoRowsPlaceholder>
        </ListItem>
      );
    }

    return (
      <ListItem key={leader.userId.value ?? Guid.generate().value} disableGutters>
        <ListItemAvatar>
          <IndividualAvatar individual={leader} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography>
              <TeamMemberLink onClick={() => openUserProfileDialog(leader.id)}>
                {leader.profile?.firstName} {leader.profile?.lastName}
              </TeamMemberLink>
            </Typography>
          }
        ></ListItemText>
        {!hideCommentButton && (
          <CommentsButton
            commentThreads={commentThreads}
            hideUnreadCount={true}
            field={ProposalField.TeamLeader}
            onCommentsClicked={
              onCommentsClicked
                ? () => {
                    onCommentsClicked(ProposalField.TeamLeader, leader.fullName);
                  }
                : undefined
            }
            toolTip={
              commentThreads?.some((thread) => thread.field.isEqualTo(ProposalField.TeamLeader))
                ? "Comments"
                : "No Comments"
            }
          />
        )}
      </ListItem>
    );
  }

  /**
   * Renders a single vendor-team member's info as a list item
   *
   * @param member The member's info to render
   */
  function renderMember(member: Individual) {
    return (
      <ListItem key={member.userId.value ?? Guid.generate().value} disableGutters>
        <ListItemAvatar>
          <IndividualAvatar individual={member} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography>
              <TeamMemberLink onClick={() => openUserProfileDialog(member.id)}>
                {member.profile?.firstName} {member.profile?.lastName}
              </TeamMemberLink>
            </Typography>
          }
        ></ListItemText>
        {!hideCommentButton && (
          <CommentsButton
            field={ProposalField.TeamMember(member.userId)}
            commentThreads={commentThreads}
            hideUnreadCount={true}
            toolTip={
              commentThreads?.some((thread) => thread.field.isEqualTo(ProposalField.TeamMember(member.userId)))
                ? "Comments"
                : "No Comments"
            }
            onCommentsClicked={
              onCommentsClicked
                ? () => {
                    onCommentsClicked(ProposalField.TeamMember(member.userId), member.fullName);
                  }
                : undefined
            }
          />
        )}
      </ListItem>
    );
  }

  return (
    <MainContainer className={className}>
      <TeamContainer>
        <TeamList>
          <TeamListSubheader>
            <span>Team Leader</span>
          </TeamListSubheader>
          {leader ? (
            <>{renderLeader(leader)}</>
          ) : (
            <ListItem disableGutters>
              <NoRowsPlaceholder>None</NoRowsPlaceholder>
            </ListItem>
          )}
        </TeamList>
        <TeamList>
          <TeamListSubheader>
            <span>Team Members</span>
            {!hideCommentButton && (
              <CommentsButton
                field={ProposalField.Team}
                hideUnreadCount={true}
                commentThreads={commentThreads?.filter((thread) => thread.field.isEqualTo(ProposalField.Team))}
                toolTip={
                  commentThreads?.some((thread) => thread.field.isEqualTo(ProposalField.Team))
                    ? "Comments"
                    : "No Comments"
                }
                onCommentsClicked={
                  onCommentsClicked
                    ? () => {
                        onCommentsClicked(ProposalField.Team);
                      }
                    : undefined
                }
              />
            )}
          </TeamListSubheader>
          {(!members || members.length < 1) && (
            <ListItem disableGutters>
              <NoRowsPlaceholder>None</NoRowsPlaceholder>
            </ListItem>
          )}
          {members?.map((member: Individual) => renderMember(member))}
        </TeamList>
      </TeamContainer>
    </MainContainer>
  );
}
